import React, {useEffect, useRef} from 'react'
import PropTypes from 'prop-types'
import {useLocation} from 'react-router-dom'
import parse from 'html-react-parser'

import {Box} from '@salesforce/retail-react-app/app/components/shared/ui'
import {Page} from '@salesforce/commerce-sdk-react/components'
import {useServerContext} from '@salesforce/pwa-kit-react-sdk/ssr/universal/hooks'
import {useGtm} from '../../../integrations/gtm/app/hooks/use-gtm'
import {useSiteCustomPreferences} from '../../../hooks/use-site-custom-preferences'
import useSeo from '../../../hooks/use-seo'
import {useContentAsset} from '../../../hooks/use-content-asset'

import Seo from '../../../../app/components/seo'
import {PAGEDESIGNER_TO_COMPONENT} from '../../pd-configs'
import {
    getPowerReviewAPIKeys,
    initPowerReviewsForPLPAndPDP
} from '../../../utils/power-reviews-utils'
import {dataComponentCount, carouselDataComponentCount} from '../../../utils/panel-selection-utils'
import {MAX_CACHE_AGE, STALE_WHILE_REVALIDATE} from '@salesforce/retail-react-app/app/constants'

const PDHomePage = ({pdPage}) => {
    const gtm = useGtm()
    const location = useLocation()
    const {getcustomPreferencevalue} = useSiteCustomPreferences()
    const contentAsset = useContentAsset(['org-schema'])

    const {res} = useServerContext()
    if (res) {
        res.set(
            'Cache-Control',
            `s-maxage=${MAX_CACHE_AGE}, stale-while-revalidate=${STALE_WHILE_REVALIDATE}`
        )
    }

    const {seoData} = useSeo({
        pageType: 'Home'
    })

    /********** GTM  ****************/
    const intervalRef = useRef(null)

    useEffect(() => {
        if (gtm) {
            gtm.sendHomePageView('home')
        }

        if (typeof window !== 'undefined') {
            if (window.dataLayer && window.OneTrust) {
                // Clear any existing interval
                if (intervalRef.current) clearInterval(intervalRef.current)
                setTimeout(() => {
                    gtm.sendOneTrustData()
                }, 1000)
            } else {
                // Start polling and store the ID in the ref
                intervalRef.current = setInterval(() => {
                    if (window.OneTrust) {
                        clearInterval(intervalRef.current)
                        gtm.sendOneTrustData()
                    }
                }, 100)
            }
        }

        // Cleanup function
        return () => {
            if (intervalRef.current) {
                clearInterval(intervalRef.current)
            }
        }
    }, [gtm])

    /**************** Power Review ****************/
    useEffect(() => {
        const getRatings = async () => {
            const result = await getPowerReviewAPIKeys(getcustomPreferencevalue)
            if (!result) return
            if (result) {
                await initPowerReviewsForPLPAndPDP(result)
            }
        }
        getRatings()
    }, [getcustomPreferencevalue])

    useEffect(() => {
        dataComponentCount()
        setTimeout(() => carouselDataComponentCount(true), 3000)
    }, [])

    useEffect(() => {
        const targetSelector = 'onetrust-banner-sdk'
        const customClass = 'ot-buttons-fw'

        const addClassIfFound = () => {
            const el = document.getElementById(targetSelector)
            if (el && !el.classList.contains(customClass)) {
                el.classList.add(customClass)
                return true
            }
            return false
        }

        if (!addClassIfFound()) {
            const observer = new MutationObserver(() => {
                if (addClassIfFound()) {
                    observer.disconnect()
                }
            })

            observer.observe(document.body, {
                childList: true,
                subtree: true
            })

            return () => observer.disconnect()
        }
    }, [])

    return (
        <Box>
            {contentAsset && contentAsset?.['org-schema']?.body
                ? parse(contentAsset?.['org-schema']?.body)
                : ''}
            {seoData && <Seo key={location.pathname} hrefLangs={seoData.hrefLang}></Seo>}

            <Page page={pdPage} components={PAGEDESIGNER_TO_COMPONENT} />
        </Box>
    )
}

PDHomePage.propTypes = {
    /**
     * The current react router match object. (Provided internally)
     */
    pdPage: PropTypes.object
}

export default PDHomePage
